import { Box, IconButton, LinearProgress, Stack, useMediaQuery } from '@mui/material';
import SprakserviceIcon from '@web-packages/svgs/SprakserviceIcon';
import FacebookIcon from '@web-packages/svgs/Facebook';
import InstagramIcon from '@web-packages/svgs/Instagram';
import LinkedInIcon from '@web-packages/svgs/LinkedIn';
import TwitterIcon from '@web-packages/svgs/Twitter';
import Typography from '@web-packages/atoms/Typography';
import Button from '@web-packages/atoms/Button';
import colors from '@shared/design-tokens/colors';

export const AUTH_CARD_MAX_WIDTH_PX = 1040;

const fontFamilyRoboto = '"Roboto","Helvetica","Arial",sans-serif';

export type SocialIconName = 'facebook' | 'instagram' | 'linkedin' | 'twitter';

const socialIcons: Record<SocialIconName, React.ComponentType<{ fill?: string; size?: number }>> = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  linkedin: LinkedInIcon,
  twitter: TwitterIcon,
};

export type FooterSocialButton = {
  icon: SocialIconName;
  color?: string;
  size?: number;
  onClick?: () => void;
  ariaLabel?: string;
};

export type FooterLink = {
  label: string;
  /** If omitted the item renders as plain text instead of a button. */
  onClick?: () => void;
};

type CardShellProps = {
  title: string;
  description?: string;
  /** Extra content below the description in the left column. */
  leftExtra?: React.ReactNode;
  /** Right column content. */
  rightContent: React.ReactNode;
  /** Bottom action row. */
  actions?: React.ReactNode;
  /** Icon buttons shown on the left side of the footer. */
  footerSocialButtons?: FooterSocialButton[];
  /** Links/text items shown on the right side of the footer. */
  footerLinks?: FooterLink[];
  /** Show a loading bar at the top of the card. */
  loading?: boolean;
};

const CardShell = ({ title, description, leftExtra, rightContent, actions, footerSocialButtons, footerLinks, loading }: CardShellProps) => {
  const textPrimary = colors.core.darkBlue;

  const isDesktop = useMediaQuery('(min-width:1040px)');
  const isMobile = useMediaQuery('(max-width:599px)');
  // modal mode: between 600px and 1039px
  const mode = isMobile ? 'mobile' : isDesktop ? 'desktop' : 'modal';

  const renderFooter = () => (
    <>
      <Stack direction="row" spacing="4px">
        {footerSocialButtons?.map((item) => {
          const IconComponent = socialIcons[item.icon as SocialIconName];
          return (
            <IconButton key={item.icon} onClick={item.onClick} aria-label={item.ariaLabel ?? item.icon} size="small" sx={{ p: '12px' }}>
              <IconComponent fill={item.color} size={item.size ?? 20} />
            </IconButton>
          );
        })}
      </Stack>
      <Stack direction="row" spacing="8px" justifyContent="flex-start" alignItems="center" flexWrap="wrap" sx={{ '& .MuiButton-root': { fontWeight: 'normal', fontSize: '12px', width: 'fit-content', minWidth: 'unset' }, '& .MuiBadge-root': { width: 'fit-content' } }}>
        {footerLinks?.map((item) =>
          item.onClick ? (
            <Button
              key={item.label}
              color="secondary"
              label={item.label}
              onClick={item.onClick}
              preventPadding
              isSmall
            />
          ) : (
            <Box sx={{ padding: '4px 1px' }}>
              <Typography
                key={item.label}
                variant="body2"
                color={colors.gray2}
                sx={{ fontSize: '12px' }}
              >
                {item.label}
              </Typography>
            </Box>
          )
        )}
      </Stack>
    </>
  );

  return (
    <Box
      sx={{
        display: mode === 'mobile' ? 'flex' : 'block',
        flexDirection: 'column',
        minHeight: mode === 'mobile' ? '100vh' : 'auto',
        width: mode === 'desktop' ? AUTH_CARD_MAX_WIDTH_PX : mode === 'modal' ? 480 : '100%',
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          boxSizing: 'border-box',
          flex: mode === 'mobile' ? 1 : 'none',
          width: '100%',
          mx: 0,
          bgcolor: colors.white,
          border: mode === 'mobile' ? 'none' : `1px solid ${colors.gray5}`,
          borderRadius: mode === 'mobile' ? 0 : '12px',
          boxShadow: 'none',
          overflow: 'hidden',
          p: mode === 'mobile' ? 3 : 4.5,
          fontFamily: fontFamilyRoboto,
          '& .MuiButton-root, & .MuiLoadingButton-root, & .MuiOutlinedInput-root, & .MuiInputLabel-root, & .MuiFormHelperText-root':
            { fontFamily: fontFamilyRoboto },
        }}
      >
        {loading && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
            }}
          />
        )}
        <Stack spacing={mode === 'desktop' ? 4 : 3}>
          <SprakserviceIcon aria-hidden width={48} height={48} />

          <Stack
            direction={mode === 'desktop' ? 'row' : 'column'}
            spacing={mode === 'desktop' ? 6 : 3}
            alignItems="stretch"
          >
            <Stack spacing={2} flex={mode === 'desktop' ? 1 : undefined} sx={{ minWidth: 0 }}>
              <Typography
                component="h1"
                variant="h4"
                bold
                color={textPrimary}
                sx={{ lineHeight: 1.25 }}
              >
                {title}
              </Typography>
              {description && (
                <Typography
                  variant="body1"
                  color={textPrimary}
                  sx={{ lineHeight: 1.6, maxWidth: 440 }}
                >
                  {description}
                </Typography>
              )}
              {leftExtra}
            </Stack>

            <Box flex={mode === 'desktop' ? 1 : undefined} sx={{ width: '100%', minWidth: 0 }}>
              {rightContent}
            </Box>
          </Stack>

          {actions && (
            <Box>
              {actions}
            </Box>
          )}
        </Stack>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: mode === 'desktop' ? 'row' : 'column',
          alignItems: mode === 'desktop' ? 'center' : 'flex-start',
          justifyContent: mode === 'desktop' ? 'space-between' : undefined,
          height: mode === 'desktop' ? 64 : 'auto',
          pt: mode === 'desktop' ? 0 : 2,
          pb: mode === 'desktop' ? 0 : 3,
          px: mode === 'mobile' ? 1 : mode === 'modal' ? 0 : 0,
          boxSizing: 'border-box',
        }}
      >
        {renderFooter()}
      </Box>
    </Box>
  );
};

export default CardShell;
